import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../common/Modal';
import { GreenBeanProfileForm } from '../../pages/purchasing/GreenBeanProfileForm';
import { RoastProfileForm } from '../../pages/inventory/RoastProfileForm';
import { CuppingProfileForm } from '../../pages/inventory/CuppingProfileForm';
import { profileService, type RoastProfile, type CuppingProfile } from '../../services/profileService';
import { greenBeanService, type GreenBeanProfile } from '../../services/greenBeanService';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { InventoryRoastProfileModal } from './InventoryRoastProfileModal';

// Maps the profileType prop to the Casbin resource that governs write access.
// Keep in sync with backend/internal/auth/resources.go.
const PROFILE_WRITE_RESOURCE: Record<'greenBean' | 'roast' | 'cupping', string> = {
  greenBean: 'green_bean_profiles',
  roast: 'roast_profiles',
  cupping: 'cupping_sessions',
};

type ProfileData = GreenBeanProfile | RoastProfile | CuppingProfile;

interface ProfileManagerProps {
  entityType: 'ItemType' | 'LotType' | 'SampleType';
  entityId: string;
  profileType: 'greenBean' | 'roast' | 'cupping';
  existingProfile?: Partial<ProfileData>;
  initialData?: Partial<ProfileData>; // For templates/defaults
  onProfileUpdate?: () => void;
  label?: string; // Optional custom label for the button
  itemName?: string; // Used for modal titles
  readOnly?: boolean;
  fallbackText?: string; // Text to show if profile doesn't exist (instead of "Add" button)
}

export const ProfileManager: React.FC<ProfileManagerProps> = ({
  entityType,
  entityId,
  profileType,
  existingProfile,
  initialData,
  onProfileUpdate,
  label,
  itemName,
  readOnly = false,
  fallbackText,
}) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  // The parent may still force read-only for view-only contexts (e.g. ingredient
  // lists). Otherwise the permission gate alone decides — parent resources like
  // inventory:write don't imply the right to edit profiles under custom roles.
  const effectiveReadOnly = readOnly || !can(PROFILE_WRITE_RESOURCE[profileType], 'write');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSession, setModalSession] = useState(0);
  const [fetchedProfile, setFetchedProfile] = useState<Partial<ProfileData> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileExists, setProfileExists] = useState(false);

  // Cupping-specific state for one-to-many
  const [cuppingProfiles, setCuppingProfiles] = useState<CuppingProfile[]>([]);
  const [showDateList, setShowDateList] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);

  const openMainModal = useCallback(() => {
    setModalSession(prev => prev + 1);
    setIsModalOpen(true);
  }, []);

  // Effect to check if profile exists (for cupping: fetch all)
  const getCuppingProfileTimestamp = useCallback((profile: CuppingProfile) => {
    const dateValue = profile.created_at || profile.cupping_date;
    const time = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isNaN(time) ? 0 : time;
  }, []);

  const syncCuppingProfiles = useCallback(async () => {
    const profiles = await profileService.getCuppingProfileByEntity(entityType, entityId);
    if (profiles && profiles.length > 0) {
      const sortedProfiles = [...profiles].sort(
        (a, b) => getCuppingProfileTimestamp(b) - getCuppingProfileTimestamp(a)
      );
      setProfileExists(true);
      setCuppingProfiles(sortedProfiles);
      setFetchedProfile(sortedProfiles[0]);
      return sortedProfiles;
    }
    setProfileExists(false);
    setCuppingProfiles([]);
    setFetchedProfile(null);
    return [];
  }, [entityId, entityType, getCuppingProfileTimestamp]);

  useEffect(() => {
    if (existingProfile && profileType !== 'cupping') {
      setProfileExists(true);
      setFetchedProfile(existingProfile);
      return;
    }

    const checkProfile = async () => {
      try {
        let data: ProfileData | ProfileData[] | null = null;
        if (profileType === 'greenBean') {
            data = await greenBeanService.getProfileByEntity(entityType, entityId);
        } else if (profileType === 'roast') {
            data = await profileService.getRoastProfileByEntity(entityType, entityId);
        } else if (profileType === 'cupping') {
            await syncCuppingProfiles();
            return;
        }
        
        if (data && !Array.isArray(data)) {
          setProfileExists(true);
          setFetchedProfile(data);
        } else {
          setProfileExists(false);
          setFetchedProfile(null);
        }
      } catch (error) {
        console.error(`Failed to check ${profileType} profile existence`, error);
        setProfileExists(false);
      }
    };

    if (entityId) {
      checkProfile();
    }
  }, [existingProfile, entityType, entityId, profileType, syncCuppingProfiles]);

  // Effect to fetch full details when modal opens (for non-cupping profiles)
  useEffect(() => {
    if (isModalOpen && profileExists && profileType !== 'cupping') {
      const fetchProfile = async () => {
        setIsLoading(true);
        try {
          let data: ProfileData | null = null;
          if (profileType === 'greenBean') {
             const profileId = existingProfile?.id;
             if (profileId) {
               data = await greenBeanService.getById(profileId);
             } else {
               data = await greenBeanService.getProfileByEntity(entityType, entityId);
             }
          } else if (profileType === 'roast') {
            data = await profileService.getRoastProfileByEntity(entityType, entityId);
          }
          
          if (data) {
            setFetchedProfile(data);
          }
        } catch (error) {
          console.error(`Failed to fetch ${profileType} profile details`, error);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchProfile();
    }
  }, [isModalOpen, profileExists, entityType, entityId, profileType, existingProfile]);

  const getTitle = () => {
    switch (profileType) {
        case 'greenBean': return profileExists ? t('profileManager.greenBean.edit') : t('profileManager.greenBean.add');
        case 'roast': return profileExists ? t('profileManager.roast.edit') : t('profileManager.roast.add');
        case 'cupping': return isAddMode ? t('profileManager.cupping.add') : t('profileManager.cupping.view');
    }
  };

  const handleAddCupping = () => {
    setIsAddMode(true);
    setFetchedProfile(null);
    openMainModal();
  };

  const handleViewCupping = async () => {
    setIsLoading(true);
    const profiles = cuppingProfiles.length > 0 ? cuppingProfiles : await syncCuppingProfiles();
    setIsLoading(false);
    if (profiles.length === 0) {
      return;
    }
    setIsAddMode(false);
    setFetchedProfile(profiles[0]);
    setShowDateList(false);
    openMainModal();
  };

  const handleSelectCuppingDate = (profile: CuppingProfile) => {
    setIsAddMode(false);
    setFetchedProfile(profile);
    setShowDateList(false);
    openMainModal();
  };

  const handleCuppingSuccess = async () => {
    setIsModalOpen(false);
    setIsAddMode(false);
    // Re-fetch cupping profiles
    try {
      await syncCuppingProfiles();
    } catch (e) {
      console.error('Failed to re-fetch cupping profiles', e);
    }
    onProfileUpdate?.();
  };

  const renderForm = () => {
      if (isLoading) {
          return (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          );
      }

      const resolvedInitialData =
        profileType === 'cupping' && isAddMode ? undefined : fetchedProfile || existingProfile || initialData;

      const commonProps = {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          initialData: resolvedInitialData as any,
          onSuccess: profileType === 'cupping' ? handleCuppingSuccess : () => {
              setIsModalOpen(false);
              setProfileExists(true);
              onProfileUpdate?.();
          },
          onCancel: () => {
            setIsModalOpen(false);
            setIsAddMode(false);
          },
          entityType,
          entityId,
          readOnly: profileType === 'cupping' ? (isAddMode ? false : true) : effectiveReadOnly,
      };

      switch (profileType) {
          case 'greenBean': return <GreenBeanProfileForm key={`${entityType}-${entityId}-${profileType}-${modalSession}`} {...commonProps} hideLotSection />;
          case 'roast': return <RoastProfileForm key={`${entityType}-${entityId}-${profileType}-${modalSession}`} {...commonProps} />;
          case 'cupping': return <CuppingProfileForm key={`${entityType}-${entityId}-${profileType}-${modalSession}`} {...commonProps} />;
      }
  };

  const formatDate = (profile: CuppingProfile) => {
    try {
      const dateValue = profile.created_at || profile.cupping_date;
      const date = dateValue ? new Date(dateValue) : null;

      if (!date || Number.isNaN(date.getTime())) {
        return profile.cupping_date || dateValue;
      }

      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return profile.cupping_date;
    }
  };

  // Cupping profile: custom render with Add & View buttons
  if (profileType === 'cupping') {
    return (
      <>
        <div className="flex items-center justify-center gap-1.5">
          {!effectiveReadOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddCupping();
              }}
              className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-gray-300 text-gray-700/50 hover:bg-gray-400"
            >
              {t('profileManager.actions.add')}
            </button>
          )}
          {profileExists && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleViewCupping();
              }}
              className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-primary-500 text-white hover:bg-primary-700"
            >
              {t('reports.shift.view')}
            </button>
          )}
          {!profileExists && effectiveReadOnly ? (
            <div className="w-full text-center">
               <span className="text-gray-400 text-xs italic">{t('common.table.na')}</span>
            </div>
          ) : !profileExists && !effectiveReadOnly && fallbackText && (
            <span className="text-gray-400 text-xs">{fallbackText}</span>
          )}
        </div>

        {/* Date Selection Modal */}
        <div onClick={(e) => e.stopPropagation()}>
          <Modal
            isOpen={showDateList}
            onClose={() => setShowDateList(false)}
            title={t('profileManager.selectCuppingDate')}
            maxWidth="sm"
          >
            <div className="space-y-2">
              <p className="text-sm text-gray-500 mb-3">
                {t('profileManager.itemHasCuppingProfiles', { count: cuppingProfiles.length })}
              </p>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {cuppingProfiles.map((profile) => (
                  <button
                    key={profile.id}
                    onClick={() => handleSelectCuppingDate(profile)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatDate(profile)}</div>
                      <div className="text-xs text-gray-500">
                        {profile.cupping_type}
                      </div>
                    </div>
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          </Modal>
        </div>

        {/* Cupping Profile Form Modal */}
        <div onClick={(e) => e.stopPropagation()}>
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setIsAddMode(false);
            }}
            title={getTitle()}
            maxWidth="6xl"
          >
            {!isAddMode && cuppingProfiles.length > 1 && (
              <div className="flex justify-end pb-3 border-b border-gray-200 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setShowDateList(true);
                  }}
                  className="px-3 py-2 border border-primary-700 rounded-md text-sm font-medium transition-colors bg-primary-500/80 text-white hover:bg-primary-700"
                >
                  {t('profileManager.viewOtherProfiles')}
                </button>
              </div>
            )}
            {renderForm()}
          </Modal>
        </div>
      </>
    );
  }

  if (profileType === 'roast') {
    return (
      <>
        {!profileExists && effectiveReadOnly ? (
          <div className="w-full text-center">
            <span className="text-gray-400 text-sm font-medium italic select-none">{t('common.table.na')}</span>
          </div>
        ) : !profileExists && fallbackText ? (
          <span className="text-gray-400 text-xs text-center">{fallbackText}</span>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                ${profileExists
                  ? 'bg-primary-500 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-700/50 hover:bg-gray-400'
                }`}
            >
              {label || (profileExists ? t('reports.shift.view') : t('profileManager.actions.add'))}
            </button>
          </div>
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <InventoryRoastProfileModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={itemName ? `${t('inventory.modals.roastProfileTitlePrefix')} – ${itemName}` : getTitle()}
            referenceId={entityId}
            onSaved={() => {
              setProfileExists(true);
              onProfileUpdate?.();
            }}
            readOnly={effectiveReadOnly}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {!profileExists && effectiveReadOnly ? (
         <div className="w-full text-center">
            <span className="text-gray-400 text-sm font-medium italic select-none">{t('common.table.na')}</span>
         </div>
      ) : !profileExists && fallbackText ? (
        <span className="text-gray-400 text-xs text-center">{fallbackText}</span>
      ) : (
        <div className="flex justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openMainModal();
            }}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap
              ${profileExists
                ? 'bg-primary-500 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-700/50 hover:bg-gray-400'
              }`}
          >
            {label || (profileExists ? t('reports.shift.view') : t('profileManager.actions.add'))}
          </button>
        </div>
      )}


      <div onClick={(e) => e.stopPropagation()}>
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={getTitle()}
          maxWidth={'5xl'}
        >
          {renderForm()}
        </Modal>
      </div>
    </>
  );
};
